<script setup lang="ts">
/**
 * App-wide notice for an MS admin who tries to request an edit while using
 * View As. Mounted once in app.js; opened through VIEW_AS_BLOCKED_EVENT from
 * the request buttons and from the axios interceptors when the server refuses.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogTitle,
} from '@/shadcn/ui/alert-dialog'
import Button from '@/shadcn/ui/button/Button.vue'
import { eventBus } from '@/Mixins/eventbus.js'
import { VIEW_AS_BLOCKED_EVENT } from '@/utils/viewAsGuard'

const { t } = useI18n()
const open = ref(false)

function show(): void {
    open.value = true
}

onMounted(() => eventBus.on(VIEW_AS_BLOCKED_EVENT, show))
onBeforeUnmount(() => eventBus.off(VIEW_AS_BLOCKED_EVENT, show))
</script>

<template>
    <AlertDialog :open="open" @update:open="(v: boolean) => (open = v)">
        <AlertDialogContent data-shadcn class="c-view-as-blocked max-w-md gap-0 p-0">
            <div class="c-view-as-blocked__head">
                <AlertDialogTitle class="c-view-as-blocked__title">
                    {{ t('shared.navbar.viewingAs.editBlockedTitle') }}
                </AlertDialogTitle>
                <AlertDialogDescription class="c-view-as-blocked__body">
                    {{ t('shared.navbar.viewingAs.editBlockedBody') }}
                </AlertDialogDescription>
            </div>
            <div class="c-view-as-blocked__foot">
                <Button class="h-9 px-4 bg-moss text-moss-foreground hover:bg-moss/90" @click="open = false">
                    {{ t('shared.navbar.viewingAs.editBlockedDismiss') }}
                </Button>
            </div>
        </AlertDialogContent>
    </AlertDialog>
</template>

<style lang="scss">
/* Not scoped: AlertDialogContent portals to the body, out of scope reach. */
.c-view-as-blocked__head {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 24px 24px 0;
    text-align: left;
}

.c-view-as-blocked__title {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
}

.c-view-as-blocked__body {
    font-size: 14px;
    line-height: 1.6;
}

.c-view-as-blocked__foot {
    display: flex;
    justify-content: flex-end;
    padding: 20px 24px 24px;
}
</style>
